import React, { useMemo, useState } from "react";
import { PageBlockEditorProps } from "organisms/page-blocks/active-page-block/page-block-editor";
import { TextBlockConfigurationRecord } from "models/view-models/block-configurations/text-block-configuration-record";
import useAutoSave from "utilities/hooks/use-auto-save";
import TextBlockConfigurationService, {
    TextBlockConfigurationUpdatePathParams,
    TextBlockConfigurationUpdatePutParams,
} from "utilities/services/block-configurations/text-block-configuration-service";
import NotebookTextBlockRichTextEditor from "organisms/notebook-text-block-rich-text-editor/notebook-text-block-rich-text-editor";
import usePageBlockError from "utilities/hooks/domain/notebooks/pages/use-page-block-errors";
import { useRunOnUnmount } from "utilities/hooks/use-run-on-unmount";
import StringUtils from "utilities/string-utils";
import usePageLimits from "pages/notebooks/use-page-limits";

interface TextBlockEditorProps extends PageBlockEditorProps {}

export const TextBlockEditor = ({
    block,
    onBlockModified,
    onBlockSaved,
    onBlockSaving,
    onDelete,
    onDone,
    onMoveDown,
    onMoveUp,
}: TextBlockEditorProps) => {
    const { pageLimits } = usePageLimits();
    const configuration = useMemo(
        () => new TextBlockConfigurationRecord(block.configuration),
        [block.configuration]
    );
    const [content, setContent] = useState(
        configuration?.getRenderableContent() ?? ""
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const contentOnLoad = useMemo(() => content, []);
    const { update: updateApi } = TextBlockConfigurationService.useUpdate();
    const { showError } = usePageBlockError();

    const handleChange = (newContent: string): void => {
        const updatedConfiguration = configuration.with({
            content: newContent,
        });
        const updatedBlock = block.with({
            configuration: updatedConfiguration,
        });
        onBlockModified(updatedBlock);
        setContent(newContent);
    };

    const handleMoveUp = async () => {
        await onMoveUp(block);
    };

    const handleMoveDown = async () => {
        await onMoveDown(block);
    };

    const handleDelete = async () => {
        await onDelete(block);
    };

    const handleSave = async (): Promise<void> => {
        if (configuration.id == null || contentOnLoad === content) {
            return;
        }

        onBlockSaving();

        try {
            const pathParams: TextBlockConfigurationUpdatePathParams = {
                configurationId: configuration.id,
            };
            const requestData: TextBlockConfigurationUpdatePutParams = {
                content: StringUtils.toServerSafeHtml(content),
            };

            const response = await updateApi(requestData, pathParams);

            if (response.result == null || response.resultObject == null) {
                throw new Error(
                    `No response when updating text block configuration with ID: ${configuration.id}`
                );
            }

            if (response.result.hasErrors()) {
                throw new Error(response.result.listErrorMessages().join(", "));
            }

            const updatedConfiguration = response.resultObject;
            const updatedBlock = block.with({
                configuration: updatedConfiguration.with({
                    content: updatedConfiguration.getRenderableContent(),
                }),
            });

            onBlockSaved(updatedBlock);
        } catch {
            showError("updating", "block");

            onBlockSaved(block);
        }
    };

    useAutoSave({
        delayInSeconds: 2,
        onSave: handleSave,
        value: content,
    });

    useRunOnUnmount(handleSave);

    const handleDone = async () => {
        await handleSave();
        onDone?.();
    };

    return (
        <NotebookTextBlockRichTextEditor
            onChange={handleChange}
            onDelete={handleDelete}
            onMoveDown={handleMoveDown}
            onMoveUp={handleMoveUp}
            value={content}
            onDone={handleDone}
            maxLength={pageLimits?.textBlockCharacterLimit}
        />
    );
};
